/* =====================================================================
 * Sign-in return path
 *
 * A signed-out visit to a workspace URL goes to `/login?next=<path>`.
 * The Google round trip leaves the SPA, so the path rides in session
 * storage from the click to `/auth/callback`, which sends the user back.
 * Only same-origin workspace paths are accepted (no open redirect).
 * ===================================================================== */

import { activeTenantKey } from '@auw/tenant';

export const LOGIN_PATH = '/login';
const STORAGE_KEY = 'auth_return_to';
/** Paths that make no sense to return to after signing in. */
const NON_RETURNABLE = new Set(['/', LOGIN_PATH, '/auth/callback']);

/** Returns a same-origin `path?search#hash`, or null when `raw` must not be followed. */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || NON_RETURNABLE.has(url.pathname)) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** `/login`, carrying the page the visitor asked for unless it is the default landing anyway. */
export function loginPathFor(path: string, defaultPath: string): string {
  const next = safeReturnPath(path);
  return next && next !== defaultPath ? `${LOGIN_PATH}?next=${encodeURIComponent(next)}` : LOGIN_PATH;
}

export function rememberReturnPath(path: string | null): void {
  try {
    if (path) sessionStorage.setItem(activeTenantKey(STORAGE_KEY), path);
    else sessionStorage.removeItem(activeTenantKey(STORAGE_KEY));
  } catch { /* Storage can be blocked; the user then lands on the default page. */ }
}

/** Reads the remembered path once. */
export function takeReturnPath(): string | null {
  try {
    const key = activeTenantKey(STORAGE_KEY);
    const path = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return safeReturnPath(path);
  } catch {
    return null;
  }
}
